import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FiX, FiBell, FiCheckCircle, FiXCircle, FiAlertTriangle } from 'react-icons/fi';
import PropTypes from 'prop-types';
import useFcmToken from '../../hooks/useFcmToken';

const NotificationSettingsModal = ({ isOpen, onClose }) => {
  const { 
    fcmToken, 
    notificationPermissionStatus, 
    requestPermission, 
    isLoading,
    error,
    isSupported 
  } = useFcmToken();
  
  const [isRequesting, setIsRequesting] = useState(false);

  const handleToggleNotifications = async () => {
    if (notificationPermissionStatus === 'granted') {
      // For disabling, we'll need to show instructions since we can't programmatically disable
      alert('To disable notifications, please go to your browser settings > Notifications > cubenationshop.com and select "Block"');
      return;
    }

    if (notificationPermissionStatus === 'denied') {
      // Show instructions for enabling from denied state
      return;
    }

    // Request permission for 'default' state
    setIsRequesting(true);
    try {
      await requestPermission();
    } catch (err) {
      console.error('Error requesting permission:', err);
    } finally {
      setIsRequesting(false);
    }
  };
  const getStatusIcon = () => {
    switch (notificationPermissionStatus) {
      case 'granted':
        return <FiCheckCircle className="w-12 h-12 text-green-500 mx-auto" />;
      case 'denied':
        return <FiXCircle className="w-12 h-12 text-red-500 mx-auto" />;
      default:
        return <FiBell className="w-12 h-12 text-gray-400 mx-auto" />;
    }
  };

  const getStatusMessage = () => {
    switch (notificationPermissionStatus) {
      case 'granted':
        return {
          title: '🎉 Notifications Enabled!',
          description: 'You\'ll receive instant updates about new products, exclusive deals, flash sales, and order updates directly to your browser.',
          status: 'success'
        };
      case 'denied':
        return {
          title: '🔔 Notifications Blocked',
          description: 'You\'re missing out on exclusive deals and important updates!',
          status: 'error'
        };
      default:
        return {
          title: '🛎️ Stay in the Loop!',
          description: 'Get notified instantly about:',
          status: 'default'
        };
    }
  };

  const benefits = [
    { icon: '⚡', title: 'Flash Sales', description: 'Be the first to know about limited-time offers' },
    { icon: '🎁', title: 'Exclusive Deals', description: 'Get access to subscriber-only discounts' },
    { icon: '📦', title: 'Order Updates', description: 'Real-time shipping and delivery notifications' },
    { icon: '🔥', title: 'New Arrivals', description: 'First access to the latest cubes and accessories' },
    { icon: '💰', title: 'Price Drops', description: 'Know when your wishlist items go on sale' },
    { icon: '🎯', title: 'Restocks', description: 'Get notified when out-of-stock items return' }
  ];

  const message = getStatusMessage();

  if (!isSupported) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Notifications Not Supported
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FiX className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="text-center">
                    <FiAlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-600">
                      Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Safari to enable notifications.
                    </p>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">                <div className="flex justify-between items-center mb-6">
                  <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                    Notification Settings
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>

                <div className="text-center mb-6">
                  {getStatusIcon()}
                  <h4 className="text-lg font-medium mt-3 mb-2">{message.title}</h4>
                  <p className="text-gray-600">{message.description}</p>
                </div>

                {notificationPermissionStatus !== 'granted' && (
                  <div className="mb-6">                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {benefits.map((benefit) => (
                        <div key={benefit.title} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <span className="text-lg">{benefit.icon}</span>
                          <div>
                            <h5 className="font-medium text-sm text-gray-900">{benefit.title}</h5>
                            <p className="text-xs text-gray-600">{benefit.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status-specific content */}
                {notificationPermissionStatus === 'denied' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h5 className="font-medium text-red-800 mb-2">How to Enable Notifications:</h5>
                    <ol className="text-sm text-red-700 space-y-1 ml-4 list-decimal">
                      <li>Click the 🔒 lock icon or ⓘ info icon in your browser's address bar</li>
                      <li>Find "Notifications" in the permissions list</li>
                      <li>Change it from "Block" to "Allow"</li>
                      <li>Refresh this page and try again</li>
                    </ol>
                  </div>
                )}

                {fcmToken && (
                  <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">
                      ✅ <strong>You're all set!</strong> You'll receive notifications for new deals, order updates, and more.
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex space-x-3">
                  {notificationPermissionStatus === 'granted' ? (
                    <>
                      <button
                        onClick={onClose}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                      >
                        All Good! 🎉
                      </button>
                      <button
                        onClick={handleToggleNotifications}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        Disable
                      </button>
                    </>
                  ) : notificationPermissionStatus === 'denied' ? (
                    <button
                      onClick={onClose}
                      className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                    >
                      Got It
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleToggleNotifications}
                        disabled={isRequesting || isLoading}
                        className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isRequesting || isLoading ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Enabling...
                          </span>
                        ) : (
                          '🔔 Enable Notifications'
                        )}
                      </button>
                      <button
                        onClick={onClose}
                        className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        Maybe Later
                      </button>
                    </>
                  )}
                </div>

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">Error: {error}</p>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>  );
};

NotificationSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NotificationSettingsModal;
